import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { MultiSelect } from "primereact/multiselect";

import { Webhook, WebhookCreatePayload, WEBHOOK_EVENT_LABELS } from "./Webhook";
import { fetchWebhookEventTypes, WebhookEventTypeOption } from "./webhooksApi";
import FormGroup from "../../common/components/form/FormGroup";
import { selectAllDepartments } from "../departments/departmentsSlice";

interface WebhookFormValues {
  url: string;
  description?: string;
  isEnabled: boolean;
  subscribedEvents: string[];
  departmentIds: number[];
}

interface WebhookFormProps {
  webhook?: Webhook;
  onSubmit: (values: WebhookCreatePayload) => Promise<void>;
  onRotateSecret?: () => void;
}

export default function WebhookForm({ webhook, onSubmit, onRotateSecret }: WebhookFormProps) {
  const departments = useSelector(selectAllDepartments);
  const [eventTypes, setEventTypes] = useState<WebhookEventTypeOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<WebhookFormValues>({
    defaultValues: {
      url: webhook?.url ?? "",
      description: webhook?.description ?? "",
      isEnabled: webhook?.isEnabled ?? true,
      subscribedEvents: webhook?.subscribedEvents ?? [],
      departmentIds: webhook?.departmentIds ?? [],
    },
  });

  useEffect(() => {
    fetchWebhookEventTypes().then(setEventTypes);
  }, []);

  const eventOptions = eventTypes.map((e) => ({
    value: e.name,
    label: WEBHOOK_EVENT_LABELS[e.name] ?? e.name,
    wireFormat: e.name,
  }));

  const departmentOptions = departments.map((d) => ({
    value: parseInt(d.id, 10),
    label: d.name,
  }));

  const handleFormSubmit = async (values: WebhookFormValues) => {
    setIsLoading(true);
    try {
      await onSubmit({
        url: values.url.trim(),
        description: values.description?.trim() || undefined,
        isEnabled: values.isEnabled,
        subscribedEvents: values.subscribedEvents,
        departmentIds: values.departmentIds,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      {webhook && (
        <FormGroup inline label="Activ">
          <Controller
            name="isEnabled"
            control={control}
            render={({ field }) => <InputSwitch checked={field.value} onChange={(e) => field.onChange(e.value)} />}
          />
        </FormGroup>
      )}

      <FormGroup label="URL *" errorMessage={errors.url?.message}>
        <InputText
          placeholder="https://intranet.example.com/api/smartsupport-webhook"
          {...register("url", {
            required: "URL-ul este obligatoriu",
            validate: (value) => {
              const trimmed = value?.trim() ?? "";
              if (!trimmed) return "URL-ul este obligatoriu";
              if (!/^https:\/\//i.test(trimmed)) return "URL-ul trebuie sa fie HTTPS";
              try {
                new URL(trimmed);
                return true;
              } catch {
                return "URL-ul nu este valid";
              }
            },
            maxLength: { value: 2048, message: "URL-ul depaseste lungimea maxima" },
          })}
        />
      </FormGroup>

      <FormGroup label="Descriere">
        <InputText
          placeholder="Etichetare libera (ex: Intranet)"
          {...register("description", {
            maxLength: { value: 512, message: "Descrierea depaseste lungimea maxima" },
          })}
        />
      </FormGroup>

      <FormGroup label="Evenimente *" errorMessage={errors.subscribedEvents?.message}>
        <Controller
          name="subscribedEvents"
          control={control}
          rules={{ validate: (value) => (value?.length ?? 0) > 0 || "Selecteaza cel putin un eveniment" }}
          render={({ field }) => (
            <MultiSelect
              display="chip"
              placeholder="Selecteaza evenimentele"
              optionLabel="label"
              optionValue="value"
              options={eventOptions}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              filter
            />
          )}
        />
      </FormGroup>

      <FormGroup label="Departamente">
        <Controller
          name="departmentIds"
          control={control}
          render={({ field }) => (
            <MultiSelect
              display="chip"
              placeholder="Toate departamentele"
              optionLabel="label"
              optionValue="value"
              options={departmentOptions}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              filter
              showClear
            />
          )}
        />
        <small className="text-color-secondary">Lasa gol pentru a primi evenimente din toate departamentele.</small>
      </FormGroup>

      <div className="flex align-items-center justify-content-between gap-2">
        {webhook && onRotateSecret ? (
          <Button type="button" label="Roteste cheia" icon="pi pi-key" severity="help" outlined onClick={onRotateSecret} />
        ) : (
          <span />
        )}
        <Button type="submit" label="Salveaza" icon="pi pi-save" loading={isLoading} disabled={isLoading} />
      </div>
    </form>
  );
}
